import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { isReferralEligible } from "@/lib/geo";
import { Wrench, MessageSquare, Settings as SettingsIcon, Crown, Gift, Gauge, Users, ArrowLeft, LayoutDashboard, Search, Map as MapIcon, UserCircle } from "lucide-react";
import { BrandWord } from "@/components/BrandWord";
import { BrandMark } from "@/components/BrandMark";

const ROOT_PATHS = new Set([
  "/", "/login", "/signup", "/onboarding",
  "/owner", "/shop", "/admin",
  "/privacy", "/terms", "/support",
]);

export function Navbar() {
  const { user, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/messages/unread-count");
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60_000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, location.pathname]);

  const showBack = !!user && !ROOT_PATHS.has(location.pathname);
  const isShop = user?.role === "shop";
  const isOwner = user?.role === "owner";
  const tier = user?.subscription_tier || "free";
  const isPaid = tier === "pro" || tier === "premium";
  const trialDaysLeft = user?.welcome_trial_until ? Math.max(0, Math.ceil((new Date(user.welcome_trial_until).getTime() - Date.now()) / 86_400_000)) : 0;

  // Nav Items Logic
  const navItems = [];
  if (isOwner) {
    navItems.push({ label: "Dashboard", path: "/owner", icon: LayoutDashboard });
    navItems.push({ label: "Post Issue", path: "/post", icon: Wrench });
    navItems.push({ label: "Mileage", path: "/mileage", icon: Gauge });
    navItems.push({ label: "Shops", path: "/shops", icon: Users });
  }
  if (isShop) {
    navItems.push({ label: "Dashboard", path: "/shop", icon: LayoutDashboard });
    navItems.push({ label: "Browse Leads", path: "/browse", icon: Search });
    navItems.push({ label: "Profile", path: "/shop-profile", icon: UserCircle });
  }
  if (user) {
    navItems.push({ label: "Map", path: "/map", icon: MapIcon });
    navItems.push({ label: "Messages", path: "/messages", icon: MessageSquare, badge: unread });
  }

  const Logo = (
    <Link to={user ? (user.role === "admin" ? "/admin" : isShop ? "/shop" : "/owner") : "/"} className="flex items-center gap-2.5">
      <BrandMark className="w-8 h-8" />
      <BrandWord className="font-heading text-lg font-black tracking-tight" />
    </Link>
  );

  const handleExitImpersonation = async () => {
    await stopImpersonation();
    navigate("/admin");
  };

  return (
    <>
      {/* --- Impersonation Banner --- */}
      {user?._impersonating && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white text-xs font-medium flex items-center justify-center gap-2 py-1.5 px-4">
          <span>⚠ Impersonating {user.email}</span>
          <button
            onClick={handleExitImpersonation}
            className="ml-2 bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-2 py-0.5 rounded transition-colors"
          >
            Exit
          </button>
        </div>
      )}

      {/* --- MOBILE/TABLET TOP NAV --- */}
      <header className={`lg:hidden sticky top-0 z-50 bg-[rgba(9,9,11,0.8)] backdrop-blur-xl border-b border-white/10 ${user?._impersonating ? "mt-8" : ""}`}>
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {showBack && <button onClick={() => navigate(-1)} className="p-2 text-zinc-400"><ArrowLeft className="w-5 h-5" /></button>}
            {Logo}
          </div>
          <div className="flex items-center gap-3">
             {user && <Link to="/settings" className="text-zinc-400"><SettingsIcon className="w-5 h-5" /></Link>}
             {!user && <Link to="/login" className="btn-primary text-xs px-3 py-1.5">Login</Link>}
          </div>
        </div>
      </header>

      {/* --- DESKTOP SIDEBAR --- */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 bg-[#09090B] border-r border-white/5 flex-col z-[60]">
        <div className="p-8 mb-4">{Logo}</div>
        
        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <Link 
              key={item.path} 
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${location.pathname === item.path ? 'bg-white/5 text-white' : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}
            >
              <item.icon className={`w-5 h-5 ${location.pathname === item.path ? 'text-[#FF3B30]' : 'group-hover:text-[#FF3B30]'}`} strokeWidth={2} />
              <span className="font-medium tracking-tight text-sm">{item.label}</span>
              {item.badge > 0 && <span className="ml-auto bg-[#FF3B30] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{item.badge}</span>}
            </Link>
          ))}
        </nav>

        <div className="p-6 border-t border-white/5">
          {user && (
            <div className="space-y-4">
              {isShop && !isPaid && (
                <Link to="/upgrade" className="flex items-center gap-3 p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-lg text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors">
                  <Crown className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-widest">Upgrade Pro</span>
                </Link>
              )}
              <Link to="/settings" className="flex items-center gap-3 px-4 py-2 text-zinc-500 hover:text-white transition-colors">
                <SettingsIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Settings</span>
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
